import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, LinearProgress, Menu, MenuItem,
  Snackbar, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useBrand } from '../../contexts/BrandContext';
import { proposedArticlesApi } from '../../services/proposed-articles';
import { renderPreview, toPublishableHtml, wordCount } from '../../utils/markdown';
import { shareIntentFor, hasShareComposer } from '../../utils/shareIntents';
import { CHANNEL_LABEL, CHANNEL_COLOR, MOMENTUM_META } from '../../components/news/channels';
import { ChannelLogo } from '../../components/news/ChannelLogo';
import { PublishWordPressDialog } from '../../components/news/PublishWordPressDialog';
import type {
  ProposedArticle, GeneratedArticle, SocialVariant, ContentChannel, CitabilityCheck, TargetEvaluation,
} from '@traaker/shared';

const PURPLE = '#7C3AED';
type Mode = 'preview' | 'markdown' | 'html';

export default function ArticleEditor() {
  const { t } = useTranslation();
  const { articleId } = useParams<{ articleId: string }>();
  const { activeBrand } = useBrand();
  const navigate = useNavigate();

  const [row, setRow] = useState<ProposedArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [md, setMd] = useState('');
  const [mode, setMode] = useState<Mode>('preview');
  const [activeTab, setActiveTab] = useState(0);
  const [saved, setSaved] = useState(true);
  const [toast, setToast] = useState('');
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    if (!articleId || !activeBrand?.brandId) return;
    setLoading(true);
    try {
      const a = await proposedArticlesApi.getOne(articleId, activeBrand.brandId);
      setRow(a);
      setMd(a.article?.articleMarkdown || '');
    } catch (e: any) {
      setError(e.message || t('articleEditor.error.load'));
    } finally {
      setLoading(false);
    }
  }, [articleId, activeBrand?.brandId, t]);

  useEffect(() => { load(); }, [load]);

  const art = row?.article;

  // Tabs: the article (long-form) + one tab per channel that has variants.
  const variantChannels = useMemo(() => {
    const set = new Map<ContentChannel, SocialVariant[]>();
    for (const v of art?.socialVariants || []) {
      const arr = set.get(v.channel) || [];
      arr.push(v);
      set.set(v.channel, arr);
    }
    return set;
  }, [art?.socialVariants]);

  const tabs = useMemo(() => {
    const list: { key: string; label: string; color: string; count?: number }[] = [
      { key: 'article', label: t('articleEditor.tab.article'), color: '#334155' },
    ];
    for (const [ch, vs] of variantChannels) {
      list.push({ key: ch, label: CHANNEL_LABEL[ch], color: CHANNEL_COLOR[ch], count: vs.length });
    }
    return list;
  }, [variantChannels, t]);

  const scheduleSave = useCallback((nextMd: string) => {
    setSaved(false);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (!articleId || !activeBrand?.brandId) return;
      try {
        await proposedArticlesApi.updateArticle(articleId, {
          brandId: activeBrand.brandId,
          article: { articleMarkdown: nextMd, wordCount: wordCount(nextMd) },
        });
        setSaved(true);
      } catch { /* keep dirty; will retry on next edit */ }
    }, 1000);
  }, [articleId, activeBrand?.brandId]);

  const onMdChange = (v: string) => { setMd(v); scheduleSave(v); };
  const flushSave = () => { if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; } };

  // Save one edited variant back into socialVariants[] via the same article PATCH.
  // Updates local `row` optimistically so the tab reflects the edit immediately.
  const saveVariant = useCallback(async (updated: SocialVariant) => {
    if (!articleId || !activeBrand?.brandId || !row?.article) return;
    const nextVariants = (row.article.socialVariants || []).map((v) =>
      v.variantId === updated.variantId ? updated : v,
    );
    setRow((prev) => prev?.article
      ? { ...prev, article: { ...prev.article, socialVariants: nextVariants } }
      : prev);
    setSaved(false);
    try {
      await proposedArticlesApi.updateArticle(articleId, {
        brandId: activeBrand.brandId,
        article: { socialVariants: nextVariants },
      });
      setSaved(true);
    } catch { /* optimistic state kept; will re-save on next edit */ }
  }, [articleId, activeBrand?.brandId, row]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error || !row) return <Box sx={{ p: 4 }}><Alert severity="error">{error || t('articleEditor.notFound')}</Alert></Box>;

  const mom = art?.momentum ? MOMENTUM_META[art.momentum] : undefined;
  const activeKey = tabs[activeTab]?.key || 'article';

  return (
    <Box sx={{ pb: 6 }}>
      {/* top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <IconButton size="small" onClick={() => navigate('/news-watch')}><ArrowBackIcon fontSize="small" /></IconButton>
          <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 520 }}>
            {row.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip size="small" label={saved ? t('articleEditor.saved') : t('articleEditor.saving')} sx={{ bgcolor: saved ? '#ECFDF5' : '#FEF3C7', color: saved ? '#065F46' : '#92400E', fontWeight: 600 }} />
          <ArticleActionMenu md={md} title={row.title} subtitle={art?.subtitle} brandId={activeBrand?.brandId} />
        </Box>
      </Box>

      {/* header band */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" fontWeight={800} letterSpacing="-0.4px">{row.title}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .7, mt: 1 }}>
          {art?.config?.format && <Chip size="small" label={art.config.format} sx={{ bgcolor: '#F5F1FF', color: PURPLE, fontWeight: 600 }} />}
          {art?.config?.objective && <Chip size="small" label={art.config.objective} sx={{ bgcolor: '#ECFDF5', color: '#0B7A56', fontWeight: 600 }} />}
          {mom && <Chip size="small" label={t('articleEditor.momentum', { label: t(mom.label) })} sx={{ border: `1px solid ${mom.color}`, color: mom.color, fontWeight: 600, bgcolor: '#fff' }} />}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: 2, p: 3 }}>
        {/* MAIN */}
        <Box sx={{ minWidth: 0 }}>
          {/* tab strip */}
          <Box sx={{ display: 'flex', gap: .5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: .5, mb: 2, overflowX: 'auto' }}>
            {tabs.map((t, i) => (
              <Box key={t.key} onClick={() => setActiveTab(i)} sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.75, py: 1, borderRadius: 1.5, cursor: 'pointer',
                fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                bgcolor: activeTab === i ? '#F5F1FF' : 'transparent', color: activeTab === i ? PURPLE : 'text.secondary',
              }}>
                {t.key === 'article'
                  ? <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: t.color }} />
                  : <ChannelLogo channel={t.key as ContentChannel} size={15} />}
                {t.label}
                {t.count != null && <Chip size="small" label={t.count} sx={{ height: 18, fontSize: 11, fontWeight: 700, bgcolor: activeTab === i ? PURPLE : 'rgba(124,58,237,.12)', color: activeTab === i ? '#fff' : PURPLE }} />}
              </Box>
            ))}
          </Box>

          {activeKey === 'article' ? (
            <ArticlePane md={md} mode={mode} setMode={(m) => { flushSave(); setMode(m); }} onChange={onMdChange}
              title={row.title} subtitle={art?.subtitle} wordCount={art?.wordCount} />
          ) : (
            <ChannelPane channel={activeKey as ContentChannel} variants={variantChannels.get(activeKey as ContentChannel) || []} onToast={setToast} onSaveVariant={saveVariant} />
          )}
        </Box>

        {/* SIDEBAR */}
        <Box sx={{ position: { md: 'sticky' }, top: 80, alignSelf: 'start' }}>
          <GeoSidebar art={art} target={activeKey} articleId={articleId!} brandId={activeBrand?.brandId || ''} onToast={setToast} />
        </Box>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')}
        message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ---- article pane ----

function ArticlePane({ md, mode, setMode, onChange, title, subtitle, wordCount: wc }: {
  md: string; mode: Mode; setMode: (m: Mode) => void; onChange: (v: string) => void;
  title: string; subtitle?: string; wordCount?: number;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: .3, color: 'text.secondary' }}>
          {wc ? t('articleEditor.articleWithWords', { count: wc }) : t('articleEditor.tab.article')}
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, m) => m && setMode(m)}>
          <ToggleButton value="preview" sx={{ textTransform: 'none', px: 1.5 }}>{t('articleEditor.mode.preview')}</ToggleButton>
          <ToggleButton value="markdown" sx={{ textTransform: 'none', px: 1.5 }}>Markdown</ToggleButton>
          <ToggleButton value="html" sx={{ textTransform: 'none', px: 1.5 }}>HTML</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {mode === 'preview' && (
        <Box sx={{ p: 3.5, fontSize: 15.5, lineHeight: 1.75, '& a': { color: PURPLE } }}
          dangerouslySetInnerHTML={{ __html: (subtitle ? `<p style="font-size:17px;color:#5A5A6E;margin:0 0 20px">${subtitle}</p>` : '') + renderPreview(md) }} />
      )}
      {mode === 'markdown' && (
        <Box component="textarea" value={md} onChange={(e: any) => onChange(e.target.value)}
          sx={{ width: '100%', minHeight: 480, border: 'none', outline: 'none', resize: 'vertical', p: 3.5, fontFamily: 'ui-monospace, monospace', fontSize: 13.5, lineHeight: 1.7, bgcolor: 'transparent', color: 'text.primary' }} />
      )}
      {mode === 'html' && (
        <Box component="pre" sx={{ p: 3.5, m: 0, overflowX: 'auto', fontFamily: 'ui-monospace, monospace', fontSize: 12.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {toPublishableHtml(md, { title, description: subtitle })}
        </Box>
      )}
    </Box>
  );
}

// ---- channel pane (variants) ----

function ChannelPane({ channel, variants, onToast, onSaveVariant }: {
  channel: ContentChannel; variants: SocialVariant[]; onToast: (m: string) => void;
  onSaveVariant: (v: SocialVariant) => void;
}) {
  const { t } = useTranslation();
  return (
    <Stack spacing={1.5}>
      {variants.map((v) => <VariantCard key={v.variantId} channel={channel} variant={v} onToast={onToast} onSave={onSaveVariant} />)}
      {variants.length === 0 && <Alert severity="info">{t('articleEditor.noVariants')}</Alert>}
    </Stack>
  );
}

function variantText(v: SocialVariant): string {
  if (v.text) return v.text;
  const s = v.structured as any;
  if (Array.isArray(s)) {
    return s.map((it: any) => {
      // carousel slide
      if (it.headline || it.body) return `${it.slide ? `Slide ${it.slide} — ` : ''}${it.headline || ''}${it.body ? `\n${it.body}` : ''}`;
      // video/tiktok script dialogue: {t, line, visual?}
      if (it.line) return `${it.t ? `[${it.t}] ` : ''}${it.line}${it.visual ? `\n  🎬 ${it.visual}` : ''}`;
      // thread tweet: {n, text}
      if (it.text) return `${it.n ? `${it.n}. ` : ''}${it.text}`;
      return JSON.stringify(it);
    }).join('\n\n');
  }
  return '';
}

// A video/tiktok script variant: structured array of {t, line, visual?}.
function isScript(v: SocialVariant): boolean {
  return v.kind === 'script' && Array.isArray(v.structured) && (v.structured as any[]).some((x) => x && x.line);
}

// An X thread: structured array of {n, text}. Rendered tweet-by-tweet with a
// per-tweet copy button — X has no web API to post a full thread, so the user
// posts tweet 1 (via the Publier sur X button) then replies with each subsequent
// tweet, copying them one at a time here.
function isThread(v: SocialVariant): boolean {
  return v.kind === 'thread' && Array.isArray(v.structured) && (v.structured as any[]).some((x) => x && x.text);
}

// Recompute a variant's charCount from its (possibly-edited) content so the
// header chip stays honest after an edit.
function recomputeCharCount(v: SocialVariant): number {
  return variantText(v).length;
}

function ThreadBody({ variant, onToast, onSave }: { variant: SocialVariant; onToast: (m: string) => void; onSave: (v: SocialVariant) => void }) {
  const { t: tr } = useTranslation();
  const [tweets, setTweets] = useState<any[]>(() => ((variant.structured as any[]) || []).map((t) => ({ ...t })));
  useEffect(() => { setTweets(((variant.structured as any[]) || []).map((t) => ({ ...t }))); }, [variant.variantId]);

  const copyTweet = async (text: string, n: number) => {
    try { await navigator.clipboard.writeText(text); onToast(tr('articleEditor.thread.copied', { n })); } catch { /* no-op */ }
  };
  const editTweet = (i: number, text: string) => setTweets((prev) => prev.map((t, j) => (j === i ? { ...t, text } : t)));
  const commit = () => {
    const next: SocialVariant = { ...variant, structured: tweets };
    onSave({ ...next, charCount: recomputeCharCount(next) });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25, fontStyle: 'italic' }}>
        {tr('articleEditor.thread.intro')}
      </Typography>
      <Stack spacing={1}>
        {tweets.map((it, i) => {
          const n = it.n ?? i + 1;
          const txt = it.text || '';
          return (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#000', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: .2 }}>{n}</Box>
              <Box sx={{ flex: 1 }}>
                <TextField value={txt} onChange={(e) => editTweet(i, e.target.value)} onBlur={commit}
                  multiline fullWidth variant="standard" InputProps={{ disableUnderline: true, sx: { fontSize: 14, lineHeight: 1.5 } }} />
                <Typography variant="caption" sx={{ color: txt.length > 280 ? 'error.main' : 'text.disabled' }}>{txt.length}/280</Typography>
              </Box>
              <Button size="small" variant="text" startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                onClick={() => copyTweet(txt, n)} sx={{ textTransform: 'none', flexShrink: 0, minWidth: 0 }}>{tr('articleEditor.copy')}</Button>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

// Renders a spoken video script: timecode chip · what's SAID (bold) · the visual
// cue (muted italic, with a 🎬), so the reader instantly separates dialogue from
// direction.
function ScriptBody({ variant, onSave }: { variant: SocialVariant; onSave: (v: SocialVariant) => void }) {
  const { t } = useTranslation();
  const [lines, setLines] = useState<any[]>(() => ((variant.structured as any[]) || []).map((l) => ({ ...l })));
  useEffect(() => { setLines(((variant.structured as any[]) || []).map((l) => ({ ...l }))); }, [variant.variantId]);

  const edit = (i: number, field: 'line' | 'visual', value: string) =>
    setLines((prev) => prev.map((l, j) => (j === i ? { ...l, [field]: value } : l)));
  const commit = () => {
    const next: SocialVariant = { ...variant, structured: lines };
    onSave({ ...next, charCount: recomputeCharCount(next) });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={1.25}>
        {lines.map((it, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.25 }}>
            {it.t && (
              <Chip size="small" label={it.t} sx={{ height: 20, fontFamily: 'ui-monospace, monospace', fontSize: 11, bgcolor: '#EEF2FF', color: '#4338CA', flexShrink: 0, mt: .3 }} />
            )}
            <Box sx={{ flex: 1 }}>
              <TextField value={it.line || ''} onChange={(e) => edit(i, 'line', e.target.value)} onBlur={commit}
                multiline fullWidth variant="standard" InputProps={{ disableUnderline: true, sx: { fontSize: 14, fontWeight: 600, lineHeight: 1.5 } }} />
              {(it.visual != null) && (
                <TextField value={it.visual || ''} onChange={(e) => edit(i, 'visual', e.target.value)} onBlur={commit}
                  multiline fullWidth variant="standard" placeholder={t('articleEditor.visualCue')}
                  InputProps={{ disableUnderline: true, startAdornment: <Box component="span" sx={{ mr: .5, fontSize: 12.5 }}>🎬</Box>, sx: { fontSize: 12.5, color: 'text.secondary', fontStyle: 'italic' } }} />
              )}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function VariantCard({ channel, variant, onToast, onSave }: { channel: ContentChannel; variant: SocialVariant; onToast: (m: string) => void; onSave: (v: SocialVariant) => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const text = variantText(variant);
  const intent = shareIntentFor(channel);
  const showShare = hasShareComposer(channel);
  const brandColor = CHANNEL_COLOR[channel];

  // Structured variants (thread / script) edit their own sub-fields; everything
  // else is a single `text` string we edit inline as a textarea.
  const [draft, setDraft] = useState(text);
  useEffect(() => { setDraft(variantText(variant)); }, [variant.variantId]);
  const commitText = () => {
    if (draft === (variant.text || '')) return;
    onSave({ ...variant, text: draft, charCount: draft.length });
  };

  const doCopy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* no-op */ }
  };
  const doShare = async () => {
    const res = await intent.share(text);
    onToast(res.toast);
  };

  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, bgcolor: '#FBFAFE', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, textTransform: 'capitalize' }}>{variant.kind.replace(/_/g, ' ')}</Typography>
        <Chip size="small" label={`${variant.charCount} car.`} sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }} />
      </Box>
      {isScript(variant)
        ? <ScriptBody variant={variant} onSave={onSave} />
        : isThread(variant)
          ? <ThreadBody variant={variant} onToast={onToast} onSave={onSave} />
          : (
            <TextField value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commitText}
              multiline fullWidth variant="standard"
              InputProps={{ disableUnderline: true, sx: { p: 2, fontSize: 14, lineHeight: 1.6, alignItems: 'flex-start' } }} />
          )}
      <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#FCFBFF' }}>
        <Button fullWidth size="small" variant="outlined" startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />} onClick={doCopy}
          sx={{ textTransform: 'none' }}>{copied ? t('articleEditor.copied') : t('articleEditor.copy')}</Button>
        {showShare && (
          <Button fullWidth size="small" variant="contained" disableElevation
            startIcon={<Box sx={{ display: 'inline-flex', width: 16, height: 16, bgcolor: '#fff', borderRadius: '3px', p: '1px', alignItems: 'center', justifyContent: 'center' }}><ChannelLogo channel={channel} size={13} /></Box>}
            onClick={doShare}
            sx={{ textTransform: 'none', bgcolor: brandColor, '&:hover': { bgcolor: brandColor, filter: 'brightness(0.92)' } }}>
            {intent.label}
          </Button>
        )}
      </Box>
    </Box>
  );
}

// ---- GEO sidebar (server-evaluated per target) ----

function GeoSidebar({ art, target, articleId, brandId, onToast }: {
  art?: GeneratedArticle; target: string; articleId: string; brandId: string; onToast: (m: string) => void;
}) {
  const { t } = useTranslation();
  const [evalResult, setEvalResult] = useState<TargetEvaluation | null>(null);
  const [loading, setLoading] = useState(false);
  const cached = art?.evaluation?.byTarget?.[target];
  // Targets we've already lazily evaluated THIS session — prevents re-firing the
  // (unthrottled) lazy eval on every tab switch when the in-memory row is stale.
  const doneTargets = useRef<Record<string, TargetEvaluation>>({});

  // Lazy-load on target change: prefer the persisted cache, then this session's
  // result, else request ONE fresh evaluation from the API.
  useEffect(() => {
    if (!art || !articleId || !brandId) return;
    if (cached && !cached.partial) { setEvalResult(cached); return; }
    if (doneTargets.current[target]) { setEvalResult(doneTargets.current[target]); return; }
    let cancelled = false;
    setLoading(true);
    setEvalResult(cached || null);
    proposedArticlesApi.evaluate(articleId, { brandId, target })
      .then((r) => { if (!cancelled) { setEvalResult(r); if (!r.partial) doneTargets.current[target] = r; } })
      .catch(() => { /* keep whatever's cached */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, articleId, brandId, art?.generatedAt]);

  const regenerate = async () => {
    setLoading(true);
    try {
      const r = await proposedArticlesApi.evaluate(articleId, { brandId, target, manual: true });
      setEvalResult(r);
      onToast(t('articleEditor.eval.updated'));
    } catch (e: any) {
      // ApiError carries the API payload on `.body` (code/message), not `.code`.
      const code = e?.body?.code;
      const retryMs = e?.body?.retryAfterMs;
      if (code === 'EVAL_REGEN_MAX') onToast(t('articleEditor.eval.maxReached'));
      else if (code === 'EVAL_REGEN_COOLDOWN') {
        const secs = retryMs ? Math.ceil(retryMs / 1000) : 60;
        onToast(t('articleEditor.eval.cooldown', { secs }));
      } else onToast(t('articleEditor.eval.failed'));
    } finally { setLoading(false); }
  };

  if (!art) return null;

  const checks = evalResult?.checks || [];
  const passed = checks.filter((c) => c.passed).length;
  const isArticle = target === 'article';
  const title = isArticle ? t('articleEditor.geo.title') : t('articleEditor.geo.bestPractices', { channel: CHANNEL_LABEL[target as ContentChannel] || target });

  return (
    <Stack spacing={2}>
      <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, background: 'linear-gradient(135deg,#F7F4FE,#FBFAFF)', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: .3, color: '#6929D6' }}>✓ {title}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
              {checks.length > 0 && (
                <Typography sx={{ fontWeight: 800 }}>{passed}<Box component="span" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: 13 }}>/{checks.length}</Box></Typography>
              )}
              <IconButton size="small" onClick={regenerate} disabled={loading} title={t('articleEditor.reevaluate')}>
                {loading ? <CircularProgress size={15} /> : <RefreshIcon sx={{ fontSize: 17 }} />}
              </IconButton>
            </Box>
          </Box>
          {checks.length > 0 && (
            <LinearProgress variant="determinate" value={(passed / checks.length) * 100}
              sx={{ height: 6, borderRadius: 999, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#10B981,#34D399)' } }} />
          )}
          {isArticle && art.targetGeoQuery && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: 'block', lineHeight: 1.45 }}>
              {t('articleEditor.geo.targetQueryLabel')} <b>« {art.targetGeoQuery} »</b>
            </Typography>
          )}
        </Box>
        <Box sx={{ p: 1 }}>
          {loading && checks.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
          ) : checks.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>{t('articleEditor.eval.unavailable')}</Typography>
          ) : (
            checks.map((c) => <CheckRow key={c.id} check={c} />)
          )}
        </Box>
        <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#FCFBFF', display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <LegendDot color="#10B981" label={t('articleEditor.legend.verified')} />
          <LegendDot outline label={t('articleEditor.legend.suggested')} />
          {evalResult?.partial && <Typography variant="caption" color="warning.main">{t('articleEditor.legend.partial')}</Typography>}
        </Box>
      </Box>

      {isArticle && (art.entities?.length ?? 0) > 0 && (
        <SideCard title={t('articleEditor.side.entities')}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .6 }}>
            {art.entities.map((e) => (
              <Chip key={e.name} size="small" label={e.name}
                sx={{ opacity: e.present ? 1 : .5, border: e.present ? 'none' : '1px dashed', borderColor: 'divider', bgcolor: e.present ? '#F2F0F7' : 'transparent' }} />
            ))}
          </Box>
        </SideCard>
      )}

      {isArticle && (art.sources?.length ?? 0) > 0 && (
        <SideCard title={t('articleEditor.side.sources')}>
          <Stack spacing={.5}>
            {art.sources.map((s, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12.5 }}>
                <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{s.domain}</Box>
                <Typography variant="caption" sx={{ color: s.cited ? '#10B981' : 'text.disabled', fontWeight: 600 }}>{s.cited ? t('articleEditor.cited') : t('articleEditor.notCited')}</Typography>
              </Box>
            ))}
          </Stack>
        </SideCard>
      )}

      {isArticle && (art.readability != null) && (
        <SideCard title={t('articleEditor.side.readability')}>
          <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{art.readability}<Box component="span" sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 600, ml: .5 }}>{t('articleEditor.readabilitySuffix')}</Box></Typography>
        </SideCard>
      )}
    </Stack>
  );
}

function CheckRow({ check }: { check: CitabilityCheck }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1, borderRadius: 1, '&:hover': { bgcolor: '#F5F1FF' } }}>
      {check.passed
        ? <CheckCircleIcon sx={{ fontSize: 18, color: check.tier === 'verified' ? '#10B981' : '#C4B5E8', mt: .1 }} />
        : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'divider', mt: .1 }} />}
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: check.passed ? 'text.primary' : 'text.secondary' }}>{check.label}</Typography>
        {check.detail && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{check.detail}</Typography>}
      </Box>
    </Box>
  );
}

function LegendDot({ color, outline, label }: { color?: string; outline?: boolean; label: string }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .5 }}>
      <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color || 'transparent', border: outline ? '1.5px dashed' : 'none', borderColor: 'divider' }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>{label}</Typography>
    </Box>
  );
}

function SideCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: .3, color: 'text.secondary', display: 'block', mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}

function ArticleActionMenu({ md, title, subtitle, brandId }: { md: string; title: string; subtitle?: string; brandId?: string }) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [wpOpen, setWpOpen] = useState(false);
  const close = () => setAnchor(null);
  const copyMd = async () => { try { await navigator.clipboard.writeText(md); } catch { /* no-op */ } close(); };
  const copyHtml = async () => { try { await navigator.clipboard.writeText(toPublishableHtml(md, { title, description: subtitle })); } catch { /* no-op */ } close(); };
  return (
    <>
      <Button variant="contained" endIcon={<MoreVertIcon />} onClick={(e) => setAnchor(e.currentTarget)} sx={{ textTransform: 'none' }}>{t('articleEditor.actions')}</Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
        <MenuItem onClick={copyMd}>{t('articleEditor.copyMarkdown')}</MenuItem>
        <MenuItem onClick={copyHtml}>{t('articleEditor.copyHtml')}</MenuItem>
        {brandId && <MenuItem onClick={() => { setWpOpen(true); close(); }}>{t('articleEditor.publishWordPress')}</MenuItem>}
      </Menu>
      {brandId && (
        <PublishWordPressDialog open={wpOpen} onClose={() => setWpOpen(false)}
          brandId={brandId} title={title} articleMarkdown={md} />
      )}
    </>
  );
}
